import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import Dashboard from './pages/Dashboard';
import Members from './pages/Members';
import MemberDetail from './pages/MemberDetail';
import AddMember from './pages/AddMember';
import EditMember from './pages/EditMember';
import EditNote from './pages/EditNote';
import ManageCoaches from './pages/ManageCoaches';
import Settings from './pages/Settings';
import CsvConverter from './pages/CsvConverter';
import ProtectedRoute from './components/ProtectedRoute';
import InstallPrompt from './components/InstallPrompt';
import OfflineNotice from './components/OfflineNotice';
import UpdateNotice from './components/UpdateNotice';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import './App.css';

const { FiAlertTriangle } = FiIcons;

// Protected Settings Route that checks for admin access
const ProtectedSettingsRoute = ({ children }) => {
  const { user } = useAuth();
  let hasAccess = false;
  if (user) {
    if (user.is_admin === true) {
      hasAccess = true;
    }
    const isPeter = user.name?.toLowerCase().includes('peter') || user.username?.toLowerCase().includes('peter');
    if (isPeter) {
      hasAccess = true;
    }
  }
  return hasAccess ? children : <Navigate to="/" replace />;
};

const ApiKeyMissingBanner = () => (
  <div className="bg-red-600 text-white p-3 text-center text-sm font-medium z-[9999] relative flex items-center justify-center gap-2">
    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5" />
    <strong>Configuration Error:</strong> The <code>VITE_NOCODE_API_KEY</code> environment variable is not set. The application will not function correctly.
  </div>
);

function AppContent() {
  const isApiKeyMissing = !import.meta.env.VITE_NOCODE_API_KEY;

  return (
    <Router>
      {isApiKeyMissing && <ApiKeyMissingBanner />}
      <OfflineNotice />
      <InstallPrompt />
      <UpdateNotice />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="members" element={<Members />} />
          <Route path="members/:memberId" element={<MemberDetail />} />
          <Route path="add-member" element={<AddMember />} />
          <Route path="edit-member/:memberId" element={<EditMember />} />
          <Route path="edit-note/:noteId/:memberId" element={<EditNote />} />
          <Route path="manage-coaches" element={<ManageCoaches />} />
          <Route
            path="settings"
            element={
              <ProtectedSettingsRoute>
                <Settings />
              </ProtectedSettingsRoute>
            }
          />
          <Route
            path="csv-converter"
            element={
              <ProtectedSettingsRoute>
                <CsvConverter />
              </ProtectedSettingsRoute>
            }
          />
        </Route>
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <AppContent />
      </DataProvider>
    </AuthProvider>
  );
}
export default App;